import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  MessageCircle,
  X,
  Send,
  Search,
  User,
  MoreVertical,
  ArrowLeft,
  Archive,
  UserPlus,
  Sparkles,
  Clock,
  CheckCheck,
  AlertCircle,
  Phone,
  ChevronDown,
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const GlobalMessenger = () => {
  const { user, hasPermission } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [outboundNumbers, setOutboundNumbers] = useState([]);
  const [selectedFromNumber, setSelectedFromNumber] = useState('');
  const [users, setUsers] = useState([]);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('myhelp_token');
      const res = await axios.get(`${API_BASE}/api/messenger/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  }, []);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const token = localStorage.getItem('myhelp_token');
      const params = { status: 'active', limit: 100 };
      if (searchQuery) params.search = searchQuery;
      
      const res = await axios.get(`${API_BASE}/api/messenger/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setConversations(res.data.conversations || []);
      return res.data.conversations || [];
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
      return [];
    }
  }, [searchQuery]);

  // Opened from the HELPConnect message pop-up: show that conversation
  useEffect(() => {
    const onOpen = async (e) => {
      setIsOpen(true);
      const list = await fetchConversations();
      const conv = list.find((c) => c.id === e.detail);
      if (conv) setSelectedConversation(conv);
    };
    window.addEventListener('myhelp:open-messenger', onOpen);
    return () => window.removeEventListener('myhelp:open-messenger', onOpen);
  }, [fetchConversations]);

  // Fetch messages for a conversation
  const fetchMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('myhelp_token');
      const res = await axios.get(`${API_BASE}/api/messenger/conversations/${conversationId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const handoffs = (res.data.handoffs || []).map((h, i) => ({ ...h, id: `handoff-${i}`, is_handoff: true }));
      setMessages([...(res.data.messages || []), ...handoffs].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));
      
      // Mark as read
      await axios.post(`${API_BASE}/api/messenger/conversations/${conversationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local unread count
      fetchUnreadCount();
      fetchConversations();
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchUnreadCount, fetchConversations]);

  // Fetch outbound numbers
  const fetchOutboundNumbers = async () => {
    try {
      const token = localStorage.getItem('myhelp_token');
      const res = await axios.get(`${API_BASE}/api/admin/outbound-numbers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns { outbound_numbers: [...] } not an array directly
      const numbers = res.data.outbound_numbers || res.data || [];
      setOutboundNumbers(numbers);
      
      // Set default
      const defaultNum = numbers.find(n => n.is_default);
      if (defaultNum) {
        setSelectedFromNumber(defaultNum.phone_number);
      } else if (numbers.length > 0) {
        setSelectedFromNumber(numbers[0].phone_number);
      }
    } catch (err) {
      console.error('Failed to fetch outbound numbers:', err);
    }
  };

  // Fetch users for reassignment
  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('myhelp_token');
      const res = await axios.get(`${API_BASE}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || sending) return;
    
    try {
      setSending(true);
      const token = localStorage.getItem('myhelp_token');
      
      await axios.post(
        `${API_BASE}/api/messenger/conversations/${selectedConversation.id}/send`,
        {
          message: newMessage,
          from_number: selectedFromNumber
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNewMessage('');
      fetchMessages(selectedConversation.id);
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  // Archive conversation
  const handleArchive = async (conversationId) => {
    try {
      const token = localStorage.getItem('myhelp_token');
      await axios.post(`${API_BASE}/api/messenger/conversations/${conversationId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedConversation(null);
      setMessages([]);
      fetchConversations();
    } catch (err) {
      console.error('Failed to archive conversation:', err);
    }
  };

  // Reassign conversation
  const handleReassign = async (conversationId, newOwnerId) => {
    try {
      const token = localStorage.getItem('myhelp_token');
      await axios.post(
        `${API_BASE}/api/messenger/conversations/${conversationId}/reassign`,
        { new_owner_id: newOwnerId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchConversations();
    } catch (err) {
      console.error('Failed to reassign conversation:', err);
    }
  };

  // Generate AI summary
  const handleGenerateSummary = async (conversationId) => {
    try {
      const token = localStorage.getItem('myhelp_token');
      const res = await axios.post(
        `${API_BASE}/api/messenger/conversations/${conversationId}/generate-summary`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        alert(`Summary created successfully!\nCase Note ID: ${res.data.note_id}`);
      }
    } catch (err) {
      console.error('Failed to generate summary:', err);
      alert('Failed to generate summary. Please try again.');
    }
  };

  // Initial load
  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      fetchOutboundNumbers();
      fetchUsers();
    }
  }, [user, fetchUnreadCount]);

  // Polling for new messages
  useEffect(() => {
    if (isOpen) {
      fetchConversations();
      
      // Poll every 10 seconds when open
      pollIntervalRef.current = setInterval(() => {
        fetchConversations();
        fetchUnreadCount();
        if (selectedConversation) {
          fetchMessages(selectedConversation.id);
        }
      }, 10000);
    } else {
      // Poll less frequently when closed
      pollIntervalRef.current = setInterval(fetchUnreadCount, 30000);
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isOpen, selectedConversation, fetchConversations, fetchUnreadCount, fetchMessages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // When conversation is selected
  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
    }
  }, [selectedConversation, fetchMessages]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    }
    return formatDate(timestamp);
  };

  const getInitials = (name) => {
    if (!name) return 'P';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Don't render if user doesn't have permission
  if (!user || !hasPermission('view_communications')) {
    return null;
  }

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          "fixed bottom-6 right-6 z-50 flex items-center justify-center",
          "w-14 h-14 rounded-full shadow-lg",
          "bg-primary hover:bg-primary/90 text-primary-foreground",
          "transition-all duration-200 hover:scale-105"
        )}
        data-testid="global-messenger-btn"
      >
        <MessageCircle className="w-6 h-6" />
        {unreadCount > 0 && (
          <Badge 
            className="absolute -top-1 -right-1 h-5 min-w-[20px] flex items-center justify-center bg-red-500 text-white text-xs px-1"
            data-testid="messenger-unread-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </button>

      {/* Messenger Sheet */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent 
          side="right" 
          className="w-full sm:w-[420px] p-0 flex flex-col"
          data-testid="global-messenger-sheet"
        >
          {/* Header */}
          <SheetHeader className="p-4 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              {selectedConversation ? (
                <div className="flex items-center gap-3">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => setSelectedConversation(null)}
                    data-testid="messenger-back-btn"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <div>
                    <SheetTitle className="text-left">{selectedConversation.participant_name}</SheetTitle>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      {selectedConversation.channel === 'helpconnect' ? (
                        <Badge variant="outline" className="text-[10px] h-5" data-testid="messenger-helpconnect-badge">HELPConnect portal</Badge>
                      ) : (<><Phone className="w-3 h-3" />{selectedConversation.phone_number}</>)}
                    </p>
                  </div>
                </div>
              ) : (
                <SheetTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-primary" />
                  HELP Messages
                </SheetTitle>
              )}
              
              {selectedConversation && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" data-testid="conversation-menu-btn">
                      <MoreVertical className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleGenerateSummary(selectedConversation.id)}>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate AI Summary
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleArchive(selectedConversation.id)}
                      className="text-destructive"
                    >
                      <Archive className="w-4 h-4 mr-2" />
                      Archive Conversation
                    </DropdownMenuItem>
                    {hasPermission('manage_users') && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                            <UserPlus className="w-4 h-4 mr-2" />
                            Reassign
                            <ChevronDown className="w-4 h-4 ml-auto" />
                          </DropdownMenuItem>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent side="left">
                          {users.map(u => (
                            <DropdownMenuItem 
                              key={u.id}
                              onClick={() => handleReassign(selectedConversation.id, u.id)}
                            >
                              {u.name}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </SheetHeader>

          {/* Content */}
          {selectedConversation ? (
            // Chat view
            <>
              <ScrollArea className="flex-1 p-4">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                    <MessageCircle className="w-12 h-12 mb-2 opacity-50" />
                    <p>No messages yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg) => msg.is_handoff ? (
                      <div key={msg.id} className="flex items-center gap-2 text-[11px] text-muted-foreground" data-testid={`conversation-handoff-${msg.id}`}>
                        <div className="flex-1 h-px bg-border" />
                        <span className="flex items-center gap-1 whitespace-nowrap">
                          <UserPlus className="w-3 h-3" />
                          Moved from {msg.from_name} to {msg.to_name} · {formatDateTime(msg.timestamp)}
                        </span>
                        <div className="flex-1 h-px bg-border" />
                      </div>
                    ) : (
                      <div
                        key={msg.id}
                        className={cn(
                          "flex",
                          msg.direction === 'outbound' ? 'justify-end' : 'justify-start'
                        )}
                      >
                        <div
                          className={cn(
                            "max-w-[80%] rounded-2xl px-4 py-2",
                            msg.direction === 'outbound'
                              ? 'bg-primary text-primary-foreground rounded-br-sm'
                              : 'bg-muted rounded-bl-sm'
                          )}
                          data-testid={`message-${msg.id}`}
                        >
                          {msg.is_auto_reply && (
                            <div className="flex items-center gap-1 text-xs opacity-70 mb-1">
                              <Clock className="w-3 h-3" />
                              Auto-reply
                            </div>
                          )}
                          <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                          <div className={cn(
                            "flex items-center gap-1 mt-1",
                            msg.direction === 'outbound' ? 'justify-end' : 'justify-start'
                          )}>
                            <span className="text-[10px] opacity-70">
                              {formatTime(msg.timestamp)}
                            </span>
                            {msg.direction === 'outbound' && (
                              msg.delivery_status === 'delivered' ? (
                                <CheckCheck className="w-3 h-3 opacity-70" />
                              ) : msg.delivery_status === 'failed' ? (
                                <AlertCircle className="w-3 h-3 text-destructive" />
                              ) : null
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              {/* Message input */}
              <div className="p-4 border-t flex-shrink-0 space-y-2">
                {outboundNumbers.length > 1 && selectedConversation.channel !== 'helpconnect' && (
                  <Select value={selectedFromNumber} onValueChange={setSelectedFromNumber}>
                    <SelectTrigger className="w-full h-8 text-xs">
                      <SelectValue placeholder="Select from number" />
                    </SelectTrigger>
                    <SelectContent>
                      {outboundNumbers.map(num => (
                        <SelectItem key={num.id} value={num.phone_number}>
                          {num.label || num.phone_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                    disabled={sending}
                    className="flex-1"
                    data-testid="messenger-input"
                  />
                  <Button 
                    onClick={handleSendMessage} 
                    disabled={!newMessage.trim() || sending}
                    size="icon"
                    data-testid="messenger-send-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            // Conversation list
            <>
              {/* Search */}
              <div className="p-4 border-b flex-shrink-0">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search conversations..."
                    className="pl-10"
                    data-testid="messenger-search"
                  />
                </div>
              </div>

              {/* Conversation list */}
              <ScrollArea className="flex-1">
                {conversations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-8">
                    <MessageCircle className="w-12 h-12 mb-2 opacity-50" />
                    <p className="text-center">No conversations yet</p>
                    <p className="text-xs text-center mt-1">
                      Send an SMS from a participant&apos;s case to start a conversation
                    </p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {conversations.map((conv) => (
                      <button
                        key={conv.id}
                        onClick={() => setSelectedConversation(conv)}
                        className={cn(
                          "w-full p-4 flex items-start gap-3 hover:bg-muted/50 transition-colors text-left",
                          conv.unread_count > 0 && "bg-primary/5"
                        )}
                        data-testid={`conversation-${conv.id}`}
                      >
                        <Avatar className="h-10 w-10 flex-shrink-0">
                          <AvatarFallback className="bg-primary/10 text-primary text-sm">
                            {getInitials(conv.participant_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className={cn(
                              "font-medium truncate",
                              conv.unread_count > 0 && "font-semibold"
                            )}>
                              {conv.participant_name}
                              {conv.channel === 'helpconnect' && (
                                <Badge variant="outline" className="ml-2 text-[10px] h-5 align-middle" data-testid={`conversation-helpconnect-badge-${conv.id}`}>HELPConnect</Badge>
                              )}
                            </span>
                            <span className="text-xs text-muted-foreground flex-shrink-0">
                              {formatTime(conv.last_message_time)}
                            </span>
                          </div>
                          <p className={cn(
                            "text-sm truncate mt-0.5",
                            conv.unread_count > 0 ? "text-foreground" : "text-muted-foreground"
                          )}>
                            {conv.last_message_direction === 'outbound' && 'You: '}
                            {conv.last_message || 'No messages'}
                          </p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-muted-foreground truncate">
                              Owner: {conv.owner_name || 'Unassigned'}
                            </span>
                            {conv.unread_count > 0 && (
                              <Badge className="bg-primary text-primary-foreground text-xs h-5 min-w-[20px] flex items-center justify-center">
                                {conv.unread_count}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};

export default GlobalMessenger;
